import { useEffect, useState } from 'react';
import { receiptUrl, type Receipt } from '../lib/api';
import { ReceiptCapture } from './ReceiptCapture';

/**
 * The receipt control inside the expense editor.
 *
 * Three ways to end up attached, because all three happen in practice:
 *  - photograph one now (the common case on a phone),
 *  - pick one already uploaded to this report — a hotel folio legitimately
 *    backs several lines, which is why the FK points from line to receipt and
 *    not the other way round,
 *  - or detach, when the wrong one got attached.
 */
export function ReceiptField({
  reportId,
  receipts,
  value,
  onChange,
  disabled,
  requiredHint,
}: {
  reportId: string;
  receipts: Receipt[];
  value: string | null;
  onChange: (receiptId: string | null) => void;
  disabled?: boolean;
  requiredHint?: boolean;
}) {
  const [preview, setPreview] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  /**
   * A receipt uploaded from inside this editor will not appear in `receipts`
   * until the report reloads, so hold on to what the capture handed back.
   * Without this the file shows as a nameless "Attached receipt" for a beat.
   */
  const [justUploaded, setJustUploaded] = useState<
    { id: string; filename: string; contentType: string; previewUrl: string } | null
  >(null);

  const known = receipts.find((r) => r.id === value) ?? null;
  const attached = known ?? (justUploaded?.id === value
    ? { originalFilename: justUploaded.filename, contentType: justUploaded.contentType, needsServerTranscode: false }
    : null);
  const available = receipts.filter((r) => r.status === 'ready');

  useEffect(() => {
    let cancelled = false;
    if (!value) {
      setPreview(null);
      return () => { cancelled = true; };
    }
    // The local blob is already decoded; use it rather than a network round
    // trip for a receipt taken moments ago.
    if (justUploaded?.id === value && justUploaded.contentType !== 'application/pdf') {
      setPreview(justUploaded.previewUrl);
      return () => { cancelled = true; };
    }
    receiptUrl(value, 'thumb')
      .then((url) => { if (!cancelled) setPreview(url); })
      // A missing thumbnail is not an error: PDFs have none, and so does a
      // HEIC the browser could not decode. The icon fallback covers it.
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [value, justUploaded]);

  if (value) {
    return (
      <div>
        <span className="label">Receipt</span>
        <div className="flex items-center gap-3 rounded-lg border border-slate-300 p-2">
          {preview ? (
            <img src={preview} alt="" className="h-16 w-16 rounded object-cover" />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded bg-slate-100 text-xs text-slate-400">
              {attached?.contentType === 'application/pdf' ? 'PDF' : 'no preview'}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm">{attached?.originalFilename ?? 'Attached receipt'}</p>
            {attached?.needsServerTranscode ? (
              <p className="text-xs text-amber-700">Stored as-is — no preview on this device.</p>
            ) : null}
          </div>
          {!disabled ? (
            <button type="button" className="min-h-11 px-3 text-sm text-red-600 underline"
                    onClick={() => onChange(null)}>
              Remove
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div>
      <span className="label">Receipt</span>
      {requiredHint ? (
        <p className="mb-2 text-xs text-amber-700">This expense needs a receipt before it can be submitted.</p>
      ) : null}

      {disabled ? (
        <p className="text-sm text-slate-500">No receipt attached.</p>
      ) : (
        <div className="space-y-2">
          <ReceiptCapture
            reportId={reportId}
            onUploaded={({ receiptId, previewUrl, filename, contentType }) => {
              setJustUploaded({ id: receiptId, filename, contentType, previewUrl });
              onChange(receiptId);
            }}
          />

          {available.length > 0 ? (
            picking ? (
              <ul className="max-h-48 divide-y divide-slate-100 overflow-y-auto rounded-lg border border-slate-200">
                {available.map((r) => (
                  <li key={r.id}>
                    <button type="button" onClick={() => { onChange(r.id); setPicking(false); }}
                            className="min-h-12 w-full truncate px-3 py-2 text-left text-sm">
                      {r.originalFilename}
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <button type="button" onClick={() => setPicking(true)}
                      className="min-h-11 w-full text-sm text-brand underline">
                Use a receipt already on this report ({available.length})
              </button>
            )
          ) : null}
        </div>
      )}
    </div>
  );
}
